import { useState } from 'react';
import { useDrop } from 'react-dnd';
import { useScript, useUpdateScript } from './ScriptContext';
import { getNode } from './treeUtils';
import REGISTRY from '../blockRegistry';
import Block from './Block';
import { ItemTypes } from './dndTypes';

// A drop zone that holds exactly one expression block.
// containerPath: path to the container node (node.children holds the expression list)
export default function ExpressionSlot({ containerPath, label }) {
  const { compId, comps, clipboard } = useScript();
  const update = useUpdateScript();
  const [hovered, setHovered] = useState(false);

  const container = getNode(comps[compId].script, containerPath);
  const hasExpr = container.children && container.children.length > 0;
  const exprPath = [...containerPath, 0];

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.EXPRESSION,
    drop: (item, monitor) => {
      // If a nested ExpressionSlot already handled this drop, do nothing.
      if (monitor.didDrop()) return;

      if (item.origin === 'palette') {
        const newNode = JSON.parse(JSON.stringify(REGISTRY[item.blockType].defaultNode));
        update((script) => {
          getNode(script, containerPath).children = [newNode];
        });
      } else {
        // Move expression from canvas
        const expr = JSON.parse(JSON.stringify(
          getNode(comps[compId].script, item.path)
        ));
        update((script) => {
          // Remove from old slot
          const oldParent = getNode(script, item.path.slice(0, -1));
          oldParent.children.splice(item.path[item.path.length - 1], 1);
          // Set in this slot
          getNode(script, containerPath).children = [expr];
        });
      }
    },
    collect: (monitor) => ({
      // shallow: true — only highlight when the cursor is directly over this slot,
      // not when it is over a nested ExpressionSlot inside a child block.
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  }), [containerPath, comps, compId]);

  const highlight = isOver && canDrop;
  // canPaste drives whether the button is *revealed* (opacity/maxWidth), not whether it's in the DOM.
  const canPaste = !hasExpr && hovered && clipboard?.kind === 'expression';
  // Keep label stable during fade-out so text doesn't blank mid-animation.
  const pasteLabel = clipboard ? (REGISTRY[clipboard.node?.type]?.shortLabel || 'expression') : '';

  const pasteExpression = (e) => {
    e.stopPropagation();
    if (!clipboard || clipboard.kind !== 'expression') return;
    const newNode = JSON.parse(JSON.stringify(clipboard.node));
    update((script) => {
      getNode(script, containerPath).children = [newNode];
    });
  };

  return (
    <div style={{ display: 'inline-block', verticalAlign: 'top' }}>
      {label && (
        <div style={{
          fontSize: '0.75vmax',
          color: '#6b7280',
          marginBottom: '3px',
          fontFamily: 'Work Sans, sans-serif',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}>
          {label}
        </div>
      )}
      <div
        ref={drop}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'inline-block',
          minWidth: '60px',
          minHeight: '28px',
          border: hasExpr ? 'none' : `1.5px dashed ${highlight ? '#2563eb' : hovered ? 'rgba(37,99,235,0.35)' : 'rgba(0,0,0,0.2)'}`,
          borderRadius: '4px',
          backgroundColor: highlight ? 'rgba(37,99,235,0.07)' : (hovered && !hasExpr) ? 'rgba(37,99,235,0.03)' : 'transparent',
          transition: 'border-color 0.18s ease, background-color 0.18s ease',
          verticalAlign: 'top',
        }}
      >
        {hasExpr ? (
          <Block path={exprPath} />
        ) : (
          <div style={{
            padding: '4px 8px',
            fontSize: '0.75vmax',
            color: highlight ? '#2563eb' : '#9ca3af',
            fontFamily: 'Work Sans, sans-serif',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            transition: 'color 0.15s',
          }}>
            <span>drag expression here</span>
            {/* Always in DOM — revealed via maxWidth + opacity so exit also animates */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              maxWidth: canPaste ? '90px' : '0',
              opacity: canPaste ? 1 : 0,
              overflow: 'hidden',
              flexShrink: 0,
              marginLeft: canPaste ? '6px' : '0',
              transition: 'max-width 0.2s ease, opacity 0.16s ease, margin-left 0.2s ease',
              pointerEvents: canPaste ? 'auto' : 'none',
            }}>
              <button
                onClick={pasteExpression}
                title={`Paste: ${pasteLabel}`}
                style={{
                  fontSize: '0.7vmax',
                  padding: '1px 6px',
                  border: '1px dashed #d1d5db',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontFamily: 'Work Sans, sans-serif',
                  backgroundColor: '#ffffff',
                  color: '#6b7280',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'border-color 0.1s, color 0.1s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.color = '#2563eb'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#6b7280'; }}
              >
                paste
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
